import { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@leadsnavi/ui-kit';
import * as EditorTypeAll from '../tinymce.d';

interface Props {
  editorInstance: EditorTypeAll.Editor;
}

const fontFormats = `Calibri=Calibri;Arial=arial,helvetica,sans-serif;Arial Black=arial black,avant garde;Times New Roman=times new roman,times;Verdana=verdana,geneva;Lucida Grande=lucida grande;Menlo=menlo`;

const defaultFontFamilys = fontFormats.split(';').map(item => {
  const itemAry = item.split('=');
  return { name: itemAry[0], value: itemAry[1] };
});

// 编辑器默认字体（与editorConfig.ts中的getFullpageBodyStyle保持一致）
const DEFAULT_EDITOR_FONT = 'arial,helvetica,sans-serif';

const Fontselect = ({ editorInstance }: Props) => {
  const btnRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [selectFontFamily, setSelectFontFamily] = useState(DEFAULT_EDITOR_FONT);

  // 获取当前真实的字体
  const getCurrentActualFont = () => {
    if (!editorInstance) return DEFAULT_EDITOR_FONT;

    // 尝试从当前选中元素获取字体
    const selection = editorInstance.selection;
    const element = selection.getNode();
    let fontFamily = editorInstance.dom.getStyle(element, 'fontFamily', true);

    // 如果当前元素没有字体样式，尝试从body获取
    if (!fontFamily) {
      const body = editorInstance.getBody();
      fontFamily = editorInstance.dom.getStyle(body, 'fontFamily', true);
    }

    // 如果还是没有，使用计算样式
    if (!fontFamily && element) {
      const computedStyle = editorInstance.dom.getStyle(element, 'fontFamily');
      fontFamily = computedStyle;
    }

    // 最后兜底使用默认字体
    return fontFamily || DEFAULT_EDITOR_FONT;
  };

  useEffect(() => {
    if (editorInstance) {
      // 编辑器初始化后立即获取字体
      const initialFont = getCurrentActualFont();
      if (initialFont) {
        // Clean up font family string (remove quotes, normalize)
        const cleanFont = initialFont.replace(/['"]/g, '').toLowerCase();
        // Find matching font in our list
        const matchingFont = defaultFontFamilys.find(
          font => cleanFont.includes(font.value.toLowerCase()) || font.value.toLowerCase().includes(cleanFont)
        );
        setSelectFontFamily(matchingFont ? matchingFont.value : cleanFont);
      }

      // 监听节点变化
      editorInstance.on('NodeChange', () => {
        const currentFont = getCurrentActualFont();
        if (currentFont) {
          // Clean up font family string (remove quotes, normalize)
          const cleanFont = currentFont.replace(/['"]/g, '').toLowerCase();
          // Find matching font in our list
          const matchingFont = defaultFontFamilys.find(
            font => cleanFont.includes(font.value.toLowerCase()) || font.value.toLowerCase().includes(cleanFont)
          );
          setSelectFontFamily(matchingFont ? matchingFont.value : cleanFont);
        }
      });
    }
  }, [editorInstance]);

  const selectFontFamilyCl = (fontFamily: { name: string; value: string }) => {
    const value = fontFamily.value;
    if (!editorInstance) return;
    setSelectFontFamily(value);
    editorInstance.execCommand('FontName', false, value);
    setVisible(false);
  };

  const getCurrentFontName = () => {
    if (!selectFontFamily) return 'Arial'; // 默认显示Arial而不是空
    const matchingFont = defaultFontFamilys.find(font => font.value === selectFontFamily);
    if (matchingFont) {
      return matchingFont.name;
    }

    // 如果不在预定义列表中，尝试美化显示名称
    // 将 "arial,helvetica,sans-serif" 转换为 "Arial"
    const fontParts = selectFontFamily.split(',');
    const primaryFont = fontParts[0].trim().replace(/['"]/g, '');
    // 首字母大写
    return primaryFont.charAt(0).toUpperCase() + primaryFont.slice(1).toLowerCase();
  };

  const fontDisplayName = getCurrentFontName();

  return (
    <Popover open={visible} onOpenChange={setVisible} modal={true}>
      <PopoverTrigger asChild>
        <div
          ref={btnRef}
          className="inline-flex items-center p-1 rounded-[22px] cursor-pointer hover:bg-[#E5E5E5] w-[88px]"
          title={fontDisplayName}
        >
          <span className="truncate text-sm flex-1 min-w-0 text-left">{fontDisplayName}</span>
          <ChevronDown size={12} className="flex-shrink-0 ml-1" />
        </div>
      </PopoverTrigger>
        <PopoverContent className="p-0.5 w-auto" side="top" align="start" sideOffset={5} style={{ zIndex: 9999 }}>
          <div className="text-xs rounded-sm ">
            <div className="pt-0.5">
              {defaultFontFamilys.map((font, index) => (
                <div
                  key={index}
                  className={classnames([
                    'w-40 h-8 box-border rounded-sm px-4 py-2 flex items-center cursor-pointer hover:bg-[#E5E5E5]',
                    font.value === selectFontFamily && 'bg-blue-50 text-blue-600',
                  ])}
                  style={{ fontFamily: font.value }}
                  onClick={() => {
                    selectFontFamilyCl(font);
                  }}
                >
                  {font.name}
                  {/* {font.value === selectFontFamily && (
                    <div className="flex items-center justify-center rounded-sm ml-auto w-auto px-2 h-4 bg-blue-200 text-blue-700 text-xs">
                      default
                    </div>
                  )} */}
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
    </Popover>
  );
};

export default Fontselect;
